import { useEffect, useMemo, useRef } from "react";
import { recordChartSizingEvent } from "../utils/chartSizingDebug";

const CANVAS_MARGIN = { left: 0, right: 55, top: 0, bottom: 30 };
const DEBUG_CHART_SIZING = import.meta.env.DEV;

function detectSnapshotCause(prev, next) {
  if (!prev) return "initial-mount";
  if (
    prev.outerWidth !== next.outerWidth ||
    prev.outerHeight !== next.outerHeight
  ) {
    return "outer-resize";
  }
  if (prev.sections.length !== next.sections.length) {
    return "section-structure-change";
  }

  const sectionChanged = next.sections.some((section, index) => {
    const prevSection = prev.sections[index];
    if (!prevSection) return true;
    return (
      prevSection.id !== section.id ||
      prevSection.index !== section.index ||
      prevSection.originX !== section.originX ||
      prevSection.originY !== section.originY ||
      prevSection.height !== section.height
    );
  });

  if (sectionChanged) {
    return "section-layout-change";
  }

  return "snapshot-refresh";
}

function classifyLayoutCause(snapshotBase, previousSnapshot) {
  const sizingSource = snapshotBase.sizingTelemetry?.source || null;
  const triggerSource = snapshotBase.sizingTelemetry?.triggerSource || null;

  if (!previousSnapshot) return "initial-mount";
  if (snapshotBase.sections.length !== previousSnapshot.sections.length) {
    return "section-structure-change";
  }

  if (
    snapshotBase.outerWidth !== previousSnapshot.outerWidth ||
    snapshotBase.outerHeight !== previousSnapshot.outerHeight
  ) {
    if (
      sizingSource === "window-resize-event" ||
      sizingSource === "trusted-event" ||
      sizingSource === "workspace.fullscreen-change.immediate" ||
      sizingSource === "workspace.fullscreen-change.delayed-150ms" ||
      triggerSource === "window-resize-event"
    ) {
      return "viewport-resize";
    }

    return "workspace-layout-change";
  }

  return null;
}

function getHorizontalResizePolicy(cause) {
  switch (cause) {
    case "viewport-resize":
    case "workspace-layout-change":
    case "section-layout-change":
    case "section-structure-change":
      // Layout changes only affect the container. They must never change
      // the chart viewport or visible bar count.
      return "preserve_x_extents";
    default:
      return "preserve_x_extents";
  }
}

export function useChartLayoutSnapshot({
  chartId,
  width,
  height,
  ratio,
  sizingTelemetry,
  chartConfigs,
}) {
  const snapshotBase = useMemo(() => {
    const outerWidth = Math.max(0, Math.round(width || 0));
    const outerHeight = Math.max(0, Math.round(height || 0));
    const plotWidth = Math.max(
      0,
      outerWidth - CANVAS_MARGIN.left - CANVAS_MARGIN.right,
    );
    const plotHeight = Math.max(
      0,
      outerHeight - CANVAS_MARGIN.top - CANVAS_MARGIN.bottom,
    );

    const sections = (chartConfigs || []).map((config) => ({
      id: config.id,
      index: config.index,
      type: config.type,
      originX: Math.round(config.origin?.[0] || 0),
      originY: Math.round(config.origin?.[1] || 0),
      height: Math.round(config.height || 0),
    }));

    return {
      chartId,
      outerWidth,
      outerHeight,
      ratio,
      sizingTelemetry: sizingTelemetry || null,
      margins: CANVAS_MARGIN,
      plotWidth,
      plotHeight,
      sections,
    };
  }, [chartId, width, height, ratio, sizingTelemetry, chartConfigs]);

  const prevSnapshotRef = useRef(null);

  const snapshot = useMemo(() => {
    const structuralCause = detectSnapshotCause(
      prevSnapshotRef.current,
      snapshotBase,
    );
    const classifiedCause = classifyLayoutCause(
      snapshotBase,
      prevSnapshotRef.current,
    );
    const cause = classifiedCause || structuralCause;
    return {
      ...snapshotBase,
      cause,
      structuralCause,
      horizontalResizePolicy: getHorizontalResizePolicy(cause),
      committedAt: Date.now(),
      committedPerfAt: Math.round(performance.now()),
    };
  }, [snapshotBase]);

  useEffect(() => {
    const measureToSnapshotMs = snapshot.sizingTelemetry?.measuredAt
      ? snapshot.committedPerfAt - snapshot.sizingTelemetry.measuredAt
      : null;

    if (DEBUG_CHART_SIZING) {
      console.debug("[chart-sizing] layout snapshot committed", {
        chartId: snapshot.chartId,
        cause: snapshot.cause,
        outerWidth: snapshot.outerWidth,
        outerHeight: snapshot.outerHeight,
        plotWidth: snapshot.plotWidth,
        plotHeight: snapshot.plotHeight,
        horizontalResizePolicy: snapshot.horizontalResizePolicy,
        fitWidthSource: snapshot.sizingTelemetry?.source || null,
        fitWidthSequence: snapshot.sizingTelemetry?.sequence || null,
        measureToSnapshotMs,
        parentRect: snapshot.sizingTelemetry?.parentRect || null,
        parentBox: snapshot.sizingTelemetry?.parentBox || null,
        grandparentBox: snapshot.sizingTelemetry?.grandparentBox || null,
        sections: snapshot.sections.map((section) => ({
          id: section.id,
          index: section.index,
          type: section.type,
          originY: section.originY,
          height: section.height,
        })),
      });
    }
    recordChartSizingEvent("layoutSnapshot.committed", {
      chartId: snapshot.chartId,
      cause: snapshot.cause,
      outerWidth: snapshot.outerWidth,
      outerHeight: snapshot.outerHeight,
      plotWidth: snapshot.plotWidth,
      plotHeight: snapshot.plotHeight,
      horizontalResizePolicy: snapshot.horizontalResizePolicy,
      fitWidthSource: snapshot.sizingTelemetry?.source || null,
      fitWidthSequence: snapshot.sizingTelemetry?.sequence || null,
      measureToSnapshotMs,
    });

    prevSnapshotRef.current = snapshot;
  }, [snapshot]);

  return snapshot;
}
